import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbArrowUp, TbArrowDown, TbTrash, TbPlus, TbLoader2 } from "react-icons/tb";
import { Section } from "./SettingsControls";
import { useJustifications, type Justification } from "src/hooks/useJustifications";
import { apiRequest, ApiError } from "src/services/apiClient";
import { MAX_JUSTIFICATIONS, MAX_JUSTIFICATION_LENGTH } from "src/helpers/justifications";
import "./invoiceTemplateSettings.scss";
import "./justificationSettings.scss";

type Row = { id?: string; key: string; text: string };

let nextKey = 0;
const toRows = (items: Justification[]): Row[] => items.map((j) => ({ id: j.id, key: j.id, text: j.text }));

/**
 * Settings → Begründungen (chapter 18): the practice's numbered Begründungen offered in every line's
 * Begründung field. Edit, reorder, delete, add — saved together (PUT replaces the whole list).
 */
export function JustificationSettings() {
    const { t } = useTranslation();
    const c = (key: string, opts?: Record<string, unknown>) => t(`justifications.${key}`, opts);
    const addId = useId();
    const { items, loading, error: loadError } = useJustifications();
    const [rows, setRows] = useState<Row[]>([]);
    const [dirty, setDirty] = useState(false);
    const [newText, setNewText] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    // Follow the server list until the user starts changing it.
    useEffect(() => {
        if (!dirty) setRows(toRows(items));
    }, [items, dirty]);

    const change = (next: Row[]) => {
        setRows(next);
        setDirty(true);
        setSaved(false);
        setError(null);
    };
    const move = (index: number, by: number) => {
        const to = index + by;
        if (to < 0 || to >= rows.length) return;
        const next = [...rows];
        [next[index], next[to]] = [next[to], next[index]];
        change(next);
    };

    const duplicates = useMemo(() => {
        const seen = new Map<string, number>();
        for (const r of rows) seen.set(r.text.trim().toLowerCase(), (seen.get(r.text.trim().toLowerCase()) ?? 0) + 1);
        return new Set([...seen].filter(([k, n]) => k && n > 1).map(([k]) => k));
    }, [rows]);
    const invalid = rows.some((r) => !r.text.trim() || r.text.trim().length > MAX_JUSTIFICATION_LENGTH) || duplicates.size > 0;

    const addRow = () => {
        const text = newText.trim();
        if (!text || rows.length >= MAX_JUSTIFICATIONS) return;
        change([...rows, { key: `new-${nextKey++}`, text }]);
        setNewText("");
    };

    const save = async () => {
        if (invalid || saving) return;
        setSaving(true);
        setError(null);
        try {
            const res = await apiRequest<{ items: Justification[] }>(
                "/justifications",
                { method: "PUT", body: JSON.stringify({ items: rows.map((r) => ({ id: r.id, text: r.text.trim() })) }) },
                { "Content-Type": "application/json" },
            );
            setRows(toRows(res.items));
            setDirty(false);
            setSaved(true);
        } catch (e) {
            const code = e instanceof ApiError ? e.body?.error : undefined;
            setError(c(code === "DUPLICATE" ? "errors.duplicate" : code === "INVALID_ITEMS" ? "errors.invalid" : "errors.save"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Section title={c("settingsTitle")} description={c("settingsDescription")} bodyClassName="template-settings justification-settings">
            {loading && rows.length === 0 ? (
                <p className="template-settings__empty" role="status"><TbLoader2 aria-hidden className="template-settings__spin" /> {c("loading")}</p>
            ) : loadError ? (
                <p className="template-settings__error" role="alert">{c("errors.load")}</p>
            ) : (
                <>
                    {rows.length === 0 ? (
                        <p className="justification-settings__empty">{c("empty")}</p>
                    ) : (
                        <ol className="template-settings__list justification-settings__list">
                            {rows.map((row, i) => {
                                const dup = duplicates.has(row.text.trim().toLowerCase());
                                const empty = !row.text.trim();
                                return (
                                    <li key={row.key} className="template-settings__item justification-settings__item">
                                        <span className="justification-settings__nr" aria-hidden>{i + 1}</span>
                                        <input
                                            className="justification-settings__input"
                                            value={row.text}
                                            maxLength={MAX_JUSTIFICATION_LENGTH}
                                            aria-label={c("textN", { n: i + 1 })}
                                            aria-invalid={dup || empty}
                                            title={dup ? c("errors.duplicateRow") : undefined}
                                            onChange={(e) => change(rows.map((r) => (r.key === row.key ? { ...r, text: e.target.value } : r)))}
                                        />
                                        <div className="template-settings__actions">
                                            <button type="button" className="template-settings__icon-btn" onClick={() => move(i, -1)} disabled={i === 0} aria-label={c("moveUp", { n: i + 1 })}>
                                                <TbArrowUp aria-hidden />
                                            </button>
                                            <button type="button" className="template-settings__icon-btn" onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label={c("moveDown", { n: i + 1 })}>
                                                <TbArrowDown aria-hidden />
                                            </button>
                                            <button type="button" className="template-settings__icon-btn template-settings__icon-btn--danger" onClick={() => change(rows.filter((r) => r.key !== row.key))} aria-label={c("deleteN", { n: i + 1 })}>
                                                <TbTrash aria-hidden />
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}

                    <form className="justification-settings__add" onSubmit={(e) => { e.preventDefault(); addRow(); }}>
                        <label htmlFor={addId} className="justification-settings__add-label">{c("addLabel")}</label>
                        <div className="justification-settings__add-row">
                            <input
                                id={addId}
                                className="justification-settings__input"
                                value={newText}
                                maxLength={MAX_JUSTIFICATION_LENGTH}
                                placeholder={c("addPlaceholder")}
                                onChange={(e) => setNewText(e.target.value)}
                                disabled={rows.length >= MAX_JUSTIFICATIONS}
                            />
                            <button type="submit" className="template-settings__new" disabled={!newText.trim() || rows.length >= MAX_JUSTIFICATIONS}>
                                <TbPlus aria-hidden /> {c("add")}
                            </button>
                        </div>
                        {rows.length >= MAX_JUSTIFICATIONS && <p className="template-settings__error">{c("errors.tooMany", { max: MAX_JUSTIFICATIONS })}</p>}
                    </form>

                    {(dirty || error || saved) && (
                        <div className="template-editor__footer justification-settings__footer">
                            {error && <p className="template-settings__error" role="alert">{error}</p>}
                            {!error && dirty && invalid && <p className="template-settings__error">{c(duplicates.size > 0 ? "errors.duplicateRow" : "errors.invalid")}</p>}
                            {saved && !dirty && <p className="justification-settings__saved" role="status">{c("saved")}</p>}
                            {dirty && (
                                <>
                                    <button type="button" className="template-editor__cancel" onClick={() => { setDirty(false); setError(null); setRows(toRows(items)); }}>
                                        {c("discard")}
                                    </button>
                                    <button type="button" className="template-editor__save" onClick={() => void save()} disabled={invalid || saving}>
                                        {saving ? c("saving") : c("save")}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </>
            )}
        </Section>
    );
}
